import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

function short(tz) {
  if (!tz) return "";
  const parts = tz.split("/");
  return parts[parts.length - 1].replace(/_/g, " ");
}

/**
 * ApplianceDailyEmailCard — subscription toggle for the Appliance Depot daily
 * report email. Mirrors the Cabinet DailyEmailCard but hits /appliance/report/email/*.
 */
export default function ApplianceDailyEmailCard() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [timeVal, setTimeVal] = useState("18:00");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appliance/report/email/me");
      setSub(data);
      setTimeVal(data.send_time_local || "18:00");
    } catch {
      toast.error("Could not load email settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/appliance/report/email/me", patch);
      setSub((prev) => ({ ...prev, ...data }));
      return data;
    } finally {
      setSaving(false);
    }
  };

  const onToggle = async (active) => {
    try {
      const next = await save({ active });
      toast.success(
        active
          ? `Daily email on · ${next.send_time_local} ${short(next.timezone)}`
          : "Daily email off",
      );
    } catch {
      toast.error("Save failed");
    }
  };

  const onTimeCommit = async () => {
    if (!sub || timeVal === sub.send_time_local) return;
    if (!/^\d{2}:\d{2}$/.test(timeVal)) {
      toast.error("Use HH:MM (24h)");
      return;
    }
    try {
      await save({ send_time_local: timeVal });
      toast.success(`Time updated · ${timeVal}`);
    } catch {
      toast.error("Save failed");
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/appliance/report/email/send-now");
      toast.success("Test email sent — check your inbox");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-card p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="appliance-email-card">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading email settings…
      </div>
    );
  }

  const configured = sub?.provider_configured;
  const active = !!sub?.active;

  return (
    <div className="surface-card p-5" data-testid="appliance-email-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
            <Mail className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <div className="label-eyebrow">Daily email</div>
            <div className="font-heading text-lg font-semibold mt-0.5">
              Send this report to your inbox
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              A daily summary of confirmed orders and pipeline quotes, sent at your
              chosen local time. Quiet days (no activity) won&apos;t send an email.
            </p>
          </div>
        </div>
        <Switch
          checked={active}
          onCheckedChange={onToggle}
          disabled={!configured || saving}
          data-testid="appliance-email-toggle"
          aria-label="Toggle Appliance daily email"
        />
      </div>

      {!configured && (
        <div className="mt-4 text-xs bg-[#b45309]/10 border border-[#b45309]/40 text-[#7c5a14] rounded px-3 py-2">
          Email provider (Mailgun) isn&apos;t configured yet, so sending is disabled.
        </div>
      )}

      {active && configured && (
        <div className="mt-5 grid gap-4 sm:grid-cols-[auto_1fr_auto] items-end">
          <div>
            <Label className="text-[11px] label-eyebrow">Send time (local)</Label>
            <Input
              type="time"
              value={timeVal}
              onChange={(e) => setTimeVal(e.target.value)}
              onBlur={onTimeCommit}
              className="w-32 mt-1"
              data-testid="appliance-email-time"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            <div className="label-eyebrow">Time zone</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-foreground">{sub.timezone}</span>
              {sub.last_sent_date && (
                <span className="inline-flex items-center gap-1 text-[10px] text-[#0f766e]">
                  <Check className="w-3 h-3" /> last sent {sub.last_sent_date}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={sendNow}
            disabled={sending}
            data-testid="appliance-email-send-now"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send test now
          </Button>
        </div>
      )}
    </div>
  );
}
